import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Field, Option, makeStyles, tokens } from '@fluentui/react-components';
import { Filter16Filled, Filter16Regular } from '@fluentui/react-icons';
import { EntityColumn } from './types';
import {
  BOOLEAN_LOOKUP_OPTIONS, FilterType, NO_VALUE_TOKEN, OPERATORS, applyFilterValue, applyOperatorChange,
  clearColumnFilters, currentOp as resolveCurrentOp, filterPrefix, renderValueInput
} from './columnFilterOps';
import { claimOpenPanel, releaseOpenPanel } from './openPanelRegistry';
import { readLookupSelection, writeLookupSelection, type LookupSelection } from './picklistSelection';
import { SearchableMultiSelect } from './SearchableMultiSelect';

// Dataverse-style per-column header filter — the makeStyles (Fluent v9) rewrite of ptc-core CRM's
// Tailwind ColumnHeaderFilter (Spec 2). Clicking a column header's funnel opens a disclosure panel
// with THAT column's operator + value controls, reusing OPERATORS/renderValueInput from
// columnFilterOps (never duplicate the operator logic). Writes the same `filter.<col>.<op>`
// record shape the shared URL codec consumes.
//
// Deliberately a plain disclosure button + panel, NOT a Fluent Popover/Menu: the panel embeds live
// Dropdown/Input controls, and a portal-based popover's built-in outside-click dismissal fights the
// Dropdown's own portal.
//
// INTERACTION STANDARD (fleet contract): exactly ONE panel open at a time, and BOTH outside-click
// and Escape dismiss. Because each instance is independent, "one at a time" cannot live in local
// state — it lives in the module-level store below, which every instance subscribes to. Dismissal
// is hand-rolled rather than inherited from Popover precisely because of the portal hazard above:
// the operator Dropdown's listbox renders OUTSIDE this component's subtree, so a bare contains()
// check would read picking an operator as an outside click and close the panel mid-interaction.
// That case is pinned by a test.
//
// RENDER-LOOP HAZARD: the operator Dropdown is labelled via a sr-only <Field>, NEVER an aria-label
// or title — a controlled Fluent v9 Dropdown with an accessible-name attr hangs the page (and
// wedges vitest). Same rule holds inside renderValueInput.
//
// Fleet filter contract §6.6 rule I5 (cases C34–C39): the panel STAGES edits locally in `staged`,
// seeded from the applied `value` every time the panel opens (openPanel), and `apply()` is the
// ONLY path that calls `onChange`. Escape and outside-click route through `dismiss()`, which closes
// without committing — nothing further to reset, since `staged` reseeds from `value` on next open.
// ptc-supervision shipped I5 first (`ptc-supervision/src/components/listFilters/ColumnHeaderFilter.tsx`);
// this is the same staging behavior layered onto tax-lifecycle's own I1–I4 singleton registry below,
// not a port of supervision's separate `filterSurfaceRegistry`/`surfaceOwnership` modules (deliberate
// fork, contract §1.1; reconciling the two copies is BL-141 and out of scope here).

// Exactly one filter panel may be open across the app -- see openPanelRegistry.ts (split out
// 2026-11-02 so isFilterPanelOpen can be imported by page-level code without pulling in this
// file's own component export; that file's doc comment carries the full rationale, including why
// this is deliberately NOT a useSyncExternalStore subscription).

// Every id this panel's own controls point at (aria-controls / aria-owns). Fluent v9 renders those
// surfaces in a portal, so DOM containment cannot see them — but the id reference makes ownership
// explicit and, unlike an element-type check, distinguishes OUR listbox from anyone else's.
function isOwnedSurface(root: HTMLElement, target: HTMLElement) {
  const owned = new Set<string>();
  root.querySelectorAll('[aria-controls],[aria-owns]').forEach((el) => {
    for (const attr of ['aria-controls', 'aria-owns']) {
      const value = el.getAttribute(attr);
      if (value) {
        for (const id of value.split(/\s+/)) if (id) owned.add(id);
      }
    }
  });
  if (owned.size === 0) return false;
  for (let node: HTMLElement | null = target; node; node = node.parentElement) {
    if (node.id && owned.has(node.id)) return true;
  }
  return false;
}

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'inline-flex',
  },
  panel: {
    position: 'absolute',
    left: 0,
    top: '100%',
    zIndex: 20,
    marginTop: '4px',
    // >= 250px (Fluent Dropdown min-width) + padding, so the embedded Dropdown never overflows.
    width: '320px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: '12px',
    boxShadow: tokens.shadow16,
    textTransform: 'none',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
});

export interface ColumnHeaderFilterProps<TItem> {
  column: EntityColumn<TItem>;
  /** The full flat filter record (e.g. `list.filters`) — the APPLIED state. */
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  /**
   * Facets (§16.3, now shipped everywhere — OutreachPage and TaxReturnsPage, 2026-11-02). Per-
   * option row counts for a picklist/boolean column, PLUS (C86) the lookup combobox's "(has no
   * value)" count under the reserved {@link NO_VALUE_TOKEN} key, which a caller's own
   * `getFacetCounts` merges in via `computeNoValueCount` (see OutreachPage.tsx/TaxReturnsPage.tsx).
   * A FUNCTION, not a precomputed record — called only while the panel is open, so a page with a
   * large row array never pays for a count nobody is looking at. Omitted entirely on any future
   * page that hasn't adopted facets yet: every option then renders with no count suffix.
   */
  getFacetCounts?: () => Record<string, number>;
}

function sameRecord(a: Record<string, string>, b: Record<string, string>): boolean {
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => b[k] === a[k]);
}

export function ColumnHeaderFilter<TItem,>({ column, value, onChange, getFacetCounts }: ColumnHeaderFilterProps<TItem>) {
  const { t } = useTranslation();
  const styles = useStyles();
  const rootRef = useRef<HTMLSpanElement>(null);
  // Remembers a chosen operator before any value exists yet.
  const [opChoice, setOpChoice] = useState<string | undefined>(undefined);
  // I5: the DRAFT. Seeded from the applied `value` every time the panel opens (openPanel below),
  // which is what makes re-open show last-applied rather than abandoned edits (C36's re-open
  // clause). The operator/value controls read and write THIS, never `value`, while open.
  const [staged, setStaged] = useState<Record<string, string>>(value);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [panelId] = useState(() => `chf-${Math.random().toString(36).slice(2)}`);
  const [open, setOpen] = useState(false);

  const closeSelf = useCallback(() => setOpen(false), []);

  const setPanelOpen = useCallback(
    (next: boolean) => {
      if (next) claimOpenPanel(closeSelf);
      else releaseOpenPanel(closeSelf);
      setOpen(next);
    },
    [closeSelf]
  );

  // I5: open seeds the draft from the applied record; dismiss just closes — nothing to discard
  // beyond the draft itself, since the next openPanel() reseeds it from `value` again.
  const openPanel = useCallback(() => {
    setStaged(value);
    setOpChoice(undefined);
    setPanelOpen(true);
  }, [value, setPanelOpen]);

  const dismiss = useCallback(() => {
    setPanelOpen(false);
  }, [setPanelOpen]);

  // Outside-click dismissal. Listens on mousedown so it settles before the trigger's own click
  // toggle runs — clicking another column's trigger therefore closes this one, then opens that one.
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent) {
      const target = event.target as HTMLElement | null;
      const root = rootRef.current;
      if (!target || !root) return;
      if (root.contains(target)) return;
      // Fleet filter contract §6.6 rule I3: "outside" is scoped by OWNERSHIP, not by DOM
      // containment — and explicitly NOT by element type. Fluent v9 portals the Dropdown listbox
      // out of this subtree, but names it by id on the in-panel combobox (aria-controls) and its
      // wrapper (aria-owns). Membership in that id set is the ownership test.
      //
      // A blanket `closest('[role="listbox"]')` exemption is NOT conformant: it would also exempt
      // a listbox belonging to some other component (a row menu, a toolbar picker), so clicking
      // that would fail to dismiss this panel. Pinned by its own test.
      if (isOwnedSurface(root, target)) return;
      dismiss();
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open, dismiss]);

  // Never leave the registry pointing at a panel that no longer exists.
  useEffect(() => () => releaseOpenPanel(closeSelf), [closeSelf]);

  // §16.3: only picklist/boolean have discrete options to count, and only while the panel is
  // actually open -- a closed panel (the common case, across every column, every render) never
  // calls into the caller's row array. Computed above the `!column.filterType` early return below
  // so this hook call is unconditional (Rules of Hooks) regardless of which branch returns.
  const facetCounts = useMemo(() => {
    if (!open || !getFacetCounts) return undefined;
    if (column.filterType !== 'picklist' && column.filterType !== 'boolean') return undefined;
    return getFacetCounts();
  }, [open, getFacetCounts, column.filterType]);

  if (!column.filterType) return null;

  const isLookup = column.filterType === 'picklist' || column.filterType === 'boolean';
  const lookupOptions = column.filterType === 'boolean' ? BOOLEAN_LOOKUP_OPTIONS : column.filterOptions ?? [];
  const ops = OPERATORS[column.filterType as FilterType];
  const prefix = filterPrefix(column.key);
  // The funnel glyph is a readout of APPLIED state, never of the draft (I5: no change to the
  // active-filter indicator until Apply).
  const hasActive = Object.keys(value).some((k) => k.startsWith(prefix));
  const op = resolveCurrentOp(column, staged, opChoice);
  const opLabel = ops.find((o) => o.op === op)?.label ?? op;
  const v = staged[`${prefix}${op}`] ?? '';
  // C86: picklist/boolean never go through the {op,value} controls below -- this is the UI model
  // SearchableMultiSelect actually renders, translated from whatever {op,value} happens to be
  // staged right now (including a legacy containsData/noData from an old saved view).
  const selection: LookupSelection = isLookup ? readLookupSelection(op, v) : { excluding: false, codes: [], includeNoValue: false };

  // The ONE write path for the lookup UI -- carries the whole selection (codes + exclude + has-no-
  // value) in one go, mirroring selectOp/setFilterValue's role for the other filterTypes but
  // without their "carry old value to new op key" dance, since the selection object already IS the
  // full picture (there's nothing to carry — it's already there).
  function commitSelection(next: LookupSelection) {
    const { op: newOp, value: newVal } = writeLookupSelection(next);
    const nextStaged = { ...staged };
    clearColumnFilters(nextStaged, column.key);
    if (newVal !== '') nextStaged[`${prefix}${newOp}`] = newVal;
    setStaged(nextStaged);
    setOpChoice(newOp);
  }

  function toggle() {
    if (open) {
      dismiss();
      triggerRef.current?.focus();
    } else {
      openPanel();
    }
  }

  function selectOp(newOp: string) {
    if (newOp === op) return;
    setOpChoice(newOp);
    const { next, shouldEmit } = applyOperatorChange(column, op, newOp, staged);
    if (shouldEmit) setStaged(next);
  }

  function setFilterValue(val: string) {
    setStaged(applyFilterValue(column, op, val, staged));
  }

  // I5 lean (§6.6): Clear is an EDIT, so it stages — it does NOT call onChange and does NOT close
  // the panel. Clear-then-Escape leaves the filter applied; Clear-then-Apply removes it (C38).
  function stageClear() {
    const next = { ...staged };
    for (const k of Object.keys(next)) {
      if (k.startsWith(prefix)) delete next[k];
    }
    setStaged(next);
    setOpChoice(undefined);
  }

  // The only path that calls onChange. Rebuilt onto the CURRENT applied `value` — delete this
  // column's keys, then re-add from `staged` — so a stale draft can't resurrect a filter that was
  // cleared elsewhere (e.g. a chip) while the panel sat open. Skips the call entirely when the
  // result is identical to `value` (C37: Apply with nothing staged must not commit).
  function apply() {
    const next = { ...value };
    for (const k of Object.keys(next)) {
      if (k.startsWith(prefix)) delete next[k];
    }
    for (const [k, val] of Object.entries(staged)) {
      if (k.startsWith(prefix)) next[k] = val;
    }
    if (!sameRecord(next, value)) onChange(next);
    dismiss();
    triggerRef.current?.focus();
  }

  return (
    <span
      ref={rootRef}
      className={styles.root}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          // I2 + I5: dismiss AND discard. `staged` reseeds from `value` on next open, so there is
          // nothing further to reset here.
          dismiss();
          triggerRef.current?.focus();
        }
      }}
    >
      <Button
        ref={triggerRef}
        appearance="subtle"
        size="small"
        icon={hasActive ? <Filter16Filled /> : <Filter16Regular />}
        aria-label={`Filter ${column.label}`}
        aria-pressed={hasActive}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={toggle}
      />
      {open ? (
        <div id={panelId} role="group" aria-label={`${column.label} filter`} className={styles.panel}>
          {isLookup ? (
            // C86 (fleet filter contract v3 §16.8): one searchable multi-select combobox, never an
            // operator dropdown over a value picker.
            <SearchableMultiSelect
              label={column.label}
              options={lookupOptions}
              selected={selection.codes}
              onChange={(codes) => commitSelection({ ...selection, codes })}
              facetCounts={facetCounts}
              labels={{
                selectAll: t('common.selectAll'),
                clear: t('common.clear'),
                noMatches: t('common.noMatches'),
                valueSuffix: t('common.value'),
                searchSuffix: t('common.search'),
              }}
              noValueOption={{
                label: 'Has no value',
                checked: selection.includeNoValue,
                count: facetCounts?.[NO_VALUE_TOKEN],
                onChange: (checked) => commitSelection({ ...selection, includeNoValue: checked }),
              }}
              excludeToggle={{
                label: 'Exclude these',
                checked: selection.excluding,
                onChange: (checked) => commitSelection({ ...selection, excluding: checked }),
              }}
              onEnter={apply}
            />
          ) : (
            <>
              {/* No aria-label on the controlled Dropdown — Fluent v9 render-loop hang. */}
              <Field label={{ children: `${column.label} operator`, className: 'sr-only' }}>
                <Dropdown
                  size="small"
                  value={opLabel}
                  selectedOptions={[op]}
                  onOptionSelect={(_, d) => {
                    if (d.optionValue) selectOp(d.optionValue);
                  }}
                >
                  {ops.map((o) => (
                    <Option key={o.op} value={o.op}>
                      {o.label}
                    </Option>
                  ))}
                </Dropdown>
              </Field>
              {renderValueInput(column, op, v, setFilterValue)}
            </>
          )}
          <div className={styles.actions}>
            <Button size="small" appearance="subtle" onClick={stageClear}>
              Clear
            </Button>
            <Button size="small" appearance="primary" onClick={apply}>
              Apply
            </Button>
          </div>
        </div>
      ) : null}
    </span>
  );
}

export default ColumnHeaderFilter;
